import { Camera, Color, Group, Object3D, PerspectiveCamera, Vector3, Vector4 } from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry';

import { getWorldPerPixel } from './utils/world-per-pixel';

/** How far (meters) the line goes from its point both ways, long enough to cross the whole screen. */
export const supportLineLength = 100_000;
/** The width of the line, and the length of the dashes and the gaps between them, in pixels. */
const lineWidthPx = 2;
const dashSizePx = 8;
const gapSizePx = 6;
/**
 * How far in front of the camera the line is cut off, in multiples of the near plane. Closer to it
 * the wide lines are not drawn reliably.
 */
const nearMargin = 20;

const zAxis = new Vector3(0, 0, 1);
const tmpForward = new Vector3();
const tmpCameraPosition = new Vector3();
const tmpViewport = new Vector4();

/**
 * Whether the point is where the lines are cut off: closer to a perspective camera than the cut-off,
 * or behind it.
 */
const isCutOff = (point: Vector3, camera: Camera) =>
  (camera as PerspectiveCamera).isPerspectiveCamera &&
  point
    .clone()
    .sub(camera.getWorldPosition(tmpCameraPosition))
    .dot(camera.getWorldDirection(tmpForward)) <
    (camera as PerspectiveCamera).near * nearMargin;

/**
 * Gets the part of the line from the point in the direction, from 0 to the length, that is in front
 * of the camera, as distances from the point. Undefined if all of it is behind the camera.
 *
 * Lines that pass behind a perspective camera are not reliably drawn, some renderers drop them
 * altogether, so they are cut off in front of it.
 */
export function getVisibleRange(
  point: Vector3,
  direction: Vector3,
  camera: Camera,
  length: number,
): [number, number] | undefined {
  if (!(camera as PerspectiveCamera).isPerspectiveCamera) {
    return [0, length];
  }

  camera.getWorldDirection(tmpForward);
  const minDepth = (camera as PerspectiveCamera).near * nearMargin;
  // The depth along the line is depth + distance * slope.
  const depth = point.clone().sub(camera.getWorldPosition(tmpCameraPosition)).dot(tmpForward);
  const slope = direction.dot(tmpForward);
  let start = 0;
  let end = length;

  if (slope === 0) {
    return depth >= minDepth ? [start, end] : undefined;
  }

  const limit = (minDepth - depth) / slope;

  if (slope > 0) {
    start = Math.max(start, limit);
  } else {
    end = Math.min(end, limit);
  }

  return start < end ? [start, end] : undefined;
}

/**
 * A dashed line through a point, across the whole screen. It is drawn as two halves going out from
 * the point, so that the dashes start at the point, where the distances along the line are small
 * enough for the dashes to be precise. Each half is placed when it is rendered, to the part in front
 * of the camera, with dashes of the same size on screen as at the origin.
 */
export class PickingSupportLine extends Group {
  /** The point that the line goes through. */
  point = new Vector3();
  /** The direction of the line, of length 1. */
  direction = new Vector3(1, 0, 0);

  private materials: LineMaterial[] = [];

  /**
   * The dashes have their size on screen at the position that getOrigin returns.
   */
  constructor(
    color: Color,
    renderOrder: number,
    private getOrigin: () => Vector3,
  ) {
    super();

    for (const side of [-1, 1]) {
      this.add(this.createHalf(color, renderOrder, side));
    }
  }

  private createHalf(color: Color, renderOrder: number, side: number) {
    // A line from 0 to 1 along the x-axis, which is moved, turned and stretched along the line.
    const geometry = new LineSegmentsGeometry().setPositions([0, 0, 0, 1, 0, 0]);
    const material = new LineMaterial({
      color: color.getHex(),
      linewidth: lineWidthPx,
      dashed: true,
      dashSize: dashSizePx,
      gapSize: gapSizePx,
      depthTest: false,
      depthWrite: false,
      transparent: true,
    });
    // A dashed line discards its end caps, and the part next to the point is so close to the start
    // cap of a line this long that it is discarded with it in places. With the caps the halves meet
    // at the point in a round end, the others are off screen.
    material.onBeforeCompile = (shader) => {
      shader.fragmentShader = shader.fragmentShader.replace(
        'if ( vUv.y < - 1.0 || vUv.y > 1.0 ) discard; // discard endcaps',
        '',
      );
    };
    this.materials.push(material);

    const half = new LineSegments2(geometry, material);
    half.computeLineDistances();
    half.renderOrder = renderOrder;
    // It is only placed when it is rendered.
    half.frustumCulled = false;
    half.matrixAutoUpdate = false;

    // Typed as an Object3D, since the types of LineSegments2 leave out the camera.
    (half as Object3D).onBeforeRender = (renderer, scene, camera) => {
      // The material needs the size of the viewport for the width in pixels, like LineSegments2 sets it.
      renderer.getViewport(tmpViewport);
      material.resolution.set(tmpViewport.z, tmpViewport.w);

      const direction = this.direction.clone().multiplyScalar(side);
      const range = getVisibleRange(this.point, direction, camera, supportLineLength);
      const startPoint = range && this.point.clone().addScaledVector(direction, range[0]);
      // Where the lines are cut off the origin is not drawn, and has no size on screen behind the
      // camera, then the dashes get their size where the half starts, which is drawn.
      const origin = this.getOrigin();
      const worldPerPixel = getWorldPerPixel(
        renderer,
        camera,
        startPoint && isCutOff(origin, camera) ? startPoint : origin,
      );

      if (!range || !startPoint || worldPerPixel <= 0) {
        half.matrix.makeScale(0, 1, 1);
      } else {
        const [start, end] = range;
        // Turned about the vertical, so that it stays upright.
        const across = zAxis.clone().cross(direction);

        if (across.lengthSq() < 1e-12) {
          across.set(0, 1, 0);
        }

        across.normalize();
        half.matrix
          .makeBasis(
            direction.clone().multiplyScalar(end - start),
            across,
            direction.clone().cross(across),
          )
          .setPosition(startPoint);
        // The distance along the half goes from 0 to 1, scaled to pixels.
        material.dashScale = (end - start) / worldPerPixel;
      }

      // Forced, since the matrix is set directly instead of with updateMatrix.
      half.updateMatrixWorld(true);
    };

    return half;
  }

  /**
   * Makes the line go through the point in the direction.
   */
  place(point: Vector3, direction: Vector3) {
    this.point.copy(point);
    this.direction.copy(direction).normalize();
  }

  setColor(color: Color) {
    for (const material of this.materials) {
      material.color.copy(color);
    }
  }
}
